import cn from "@/v2-ui/utils/utils.cn"

export type UsageFilterChipOption<Value extends string> = {
  label: string
  /** `null` is the "everything" chip. */
  value: Value | null
}

export type UsageFilterChipsProps<Value extends string> = {
  options: UsageFilterChipOption<Value>[]
  value: Value | null
  onChange(value: Value | null): void
  className?: string
}

/**
 * One row of exclusive filter chips, the usage surfaces' segmented control:
 * a bordered group, the active chip filled. Every usage view that narrows
 * a list by one facet (product, play tone) renders this, so the chips look
 * the same wherever they sit.
 */
function UsageFilterChips<Value extends string>(
  props: UsageFilterChipsProps<Value>,
) {
  const { options, value, onChange, className } = props

  return (
    <div
      className={cn(
        "inline-flex items-center overflow-hidden rounded-md border border-border text-xs",
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value ?? "all"}
          type="button"
          aria-pressed={value === option.value}
          className={cn(
            "px-3 py-1 transition-colors",
            value === option.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default UsageFilterChips
